import { Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { ReplaySubject } from 'rxjs';
import { ApiService } from '../../http/api.service';
import { ApiPath } from '../../models/api-path';
import { LoginApiRequest, LoginVerifyApiRequest } from '../../models/auth/auth-form.model';
import { GenarateApiModel, TokenApiModel, VerifyApiModel } from '../../models/auth/token.model';
import { CommonModel } from '../../models/shared/commone.model';
import { UserProfileModel, UserRegisterModel } from '../../models/users/user-profile.model';
import { CheckPassExpiredModel, VerifyModel, getThaid } from '../../view-models/login';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly JWT_TOKEN = 'JWT_TOKEN';
  private readonly REFRESH_TOKEN = 'REFRESH_TOKEN';
  private readonly PROFILE = 'PROFILE';
  private readonly THAID_INFO = 'THAID_INFO';

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();
  private userProfileSubject = new ReplaySubject<UserProfileModel>();
  public userProfile$ = this.userProfileSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private permissions: NgxPermissionsService
  ) { }

  async generate(): Promise<string> {
    const result = await this.apiService.post<GenarateApiModel>(ApiPath.auth.generate);
    return result!.code;
  }

  async login(username: string, password: string, captcha: string): Promise<boolean> {
    const loginReqForm: LoginApiRequest = { username, password, verifyImageCode: captcha };
    const result = await this.apiService.post<TokenApiModel>(ApiPath.auth.login, loginReqForm);
    if (result) {
      this.setAuth(result);
      await this.loadPermissions();
      await this.loadUserProfile();
    }
    return result != null;
  }

  async loginByAccessToken(accessToken: string): Promise<boolean> {
    const loginReqForm = { accessToken };
    const result = await this.apiService.post<TokenApiModel>('/auth/auto-login', loginReqForm);
    if (result) {
      this.setAuth(result);
      await this.loadPermissions();
      await this.loadUserProfile();
    }
    return result != null;
  }

  async loadUserProfile(): Promise<UserProfileModel> {
    const user = await this.apiService.get<UserRegisterModel>(ApiPath.account.profile);
    // const user = this.authService.getUser();
    const profile: UserProfileModel = {
      email: '',
      firstName: user!.firstName,
      lastName: user!.lastName
    }
    if (profile) {
      this.setUserProfile(profile);
    }
    return profile!;
  }

  async verifyLogin(username: string, password: string): Promise<VerifyModel> {
    const loginReqForm: LoginVerifyApiRequest = { username, password };
    const result = await this.apiService.post<VerifyApiModel>(ApiPath.auth.verify, loginReqForm)

    return result!;

  }

  async logout(): Promise<boolean> {
    const logoutBody = {
      refreshToken: this.getRefreshToken()
    };
    const result = await this.apiService.post<TokenApiModel>(ApiPath.auth.logout, logoutBody);
    if (result) {
      this.clearAuth();
    }
    return result != null;
  }

  async refreshToken(): Promise<TokenApiModel> {
    const body = {
      refreshToken: this.getRefreshToken(),
      accessToken: this.getToken()
    };
    const result = await this.apiService.post<TokenApiModel>(ApiPath.auth.refreshToken, body, false);
    this.storeJwtToken(result?.accessToken ?? '');
    return result!;
  }

  setAuth(tokens: TokenApiModel) {
    this.isAuthenticatedSubject.next(true);
    this.storeTokens(tokens);
  }


  clearAuth() {
    this.removeTokens();
    this.isAuthenticatedSubject.next(false);
    this.userProfileSubject.next(new UserProfileModel());
    this.permissions.flushPermissions();
    localStorage.removeItem(this.PROFILE);
  }

  async loadPermissions() {
    if (!this.hasLogined()) { return; }
    //const permissions = await this.apiService.get<Array<string>>(ApiPath.auth.permissions);
    //if(permissions != null) this.permissions.loadPermissions(permissions)
  }

  hasLogined(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return localStorage.getItem(this.JWT_TOKEN);
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  setUserProfile(profile: UserProfileModel) {
    localStorage.setItem(this.PROFILE, JSON.stringify(profile));
    this.userProfileSubject.next(profile);
  }

  getUser(): UserProfileModel {
    const json = localStorage.getItem(this.PROFILE);
    const user = JSON.parse(json!);
    return Object.assign(new UserProfileModel, user);
  }

  private storeJwtToken(jwt: string) {
    localStorage.setItem(this.JWT_TOKEN, jwt);
  }

  private storeTokens(tokens: TokenApiModel) {
    this.storeJwtToken(tokens.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN, tokens.refreshToken);
  }

  private removeTokens() {
    localStorage.removeItem(this.JWT_TOKEN);
    localStorage.removeItem(this.REFRESH_TOKEN);
  }

  async getLoginByThaid(code: string): Promise<getThaid> {
    const loginReqForm = { code };
    const result = await this.apiService.get<getThaid>('/auth/verify-login/thaid', loginReqForm);

    if (result) {
      this.storeThaiD(result);
    }

    return result!;
  }

  private storeThaiD(thaid_info: getThaid) {
    localStorage.setItem(this.THAID_INFO, JSON.stringify(thaid_info));
  }

  public getThaidInfo(): string | null {
    return localStorage.getItem(this.THAID_INFO);
  }

  async getPassExpired(): Promise<CheckPassExpiredModel> {
    const result = await this.apiService.post<CheckPassExpiredModel>('/auth/check-password-expire');

    return result!;
  }

}
